import InfoIcon from '@mui/icons-material/Info';
import { Button, Checkbox, Dialog, DialogActions, DialogContent, FormControl, FormControlLabel, Grid, MenuItem, Select, SelectChangeEvent, TextField, Tooltip, Typography } from '@mui/material';
import { ChangeEvent, useContext, useState } from 'react';
import { Plus, X } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { PostReasonRequest, Reason } from '../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import useLogError from '../../../hooks/useLogError';

export interface Items {
  onClose: () => void;
  onConfirm: () => void;
  wasteLogReasonsCount: number;
  existingReasons: Reason[]
}

interface ActionTypes {
  description: string
}
const actionTypes: ActionTypes[] = [
  { description: 'Please Select Action Type' },
  { description: 'Issue' },
  { description: 'Receive' },
];

const CreateReasonModal = (props: Items) => {
  const { saveTransactionReasons } = useNswagClient();
  const { selectedStore } = useContext(UserContext);
  const { logError } = useLogError();
  const [description, setDescription] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [actionType, setActionType] = useState<string>(actionTypes[0].description);
  const [includeInWasteLog, setIncludeInWasteLog] = useState<boolean>(false);
  const [wasteLogSortOrder, setWasteLogSortOrder] = useState<number | null>(null);
  const [showMaxError, setShowMaxError] = useState<boolean>(false);
  const [showDuplicateSortOrderError, setShowDuplicateSortOrderError] = useState<boolean>(false);
  const { t } = useTranslation('manageTransactions');

  const saveReasons = () => {
    // Check for duplicate sort order
    if (includeInWasteLog && wasteLogSortOrder) {
      const hasDuplicate = props.existingReasons.some(
        r => r.includeInWasteLog && r.wasteLogSortOrder === wasteLogSortOrder,
      );
      if (hasDuplicate) {
        setShowDuplicateSortOrderError(true);
        return;
      }
    }
    const body: PostReasonRequest = {
      name: name,
      description: description,
      actionType: actionType,
      includeInWasteLog: includeInWasteLog,
      wasteLogSortOrder: includeInWasteLog ? wasteLogSortOrder ?? undefined : undefined,
    };
    const storeNumber = selectedStore?.storeNumber ?? '';
    saveTransactionReasons(storeNumber, body).then(() => {
      props.onConfirm();
    })
      .catch((error) => {
        logError(error);
      });
  };

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);
  };

  const handleSelect = (event: SelectChangeEvent<string>) => {
    setActionType(event.target.value);
  };

  const handleIncludeInWasteLogChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked && props.wasteLogReasonsCount >= 4) {
      setShowMaxError(true);
      return;
    }
    setShowMaxError(false);
    setIncludeInWasteLog(checked);
    // Clear sort order when removing from waste log
    if (!checked) {
      setWasteLogSortOrder(null);
    }
  };

  const handleSortOrderChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!includeInWasteLog) return;

    setShowDuplicateSortOrderError(false);
    const value = event.target.value;
    if (value === '') {
      setWasteLogSortOrder(null);
    } else {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue >= 1 && numValue <= 4) {
        setWasteLogSortOrder(numValue);
      }
    }
  };

  const handleClose = () => {
    setName('');
    setActionType(actionTypes[0].description);
    setDescription('');
    setIncludeInWasteLog(false);
    setWasteLogSortOrder(null);
    setShowDuplicateSortOrderError(false);
    setShowMaxError(false);
    props.onClose();
  };

  return (
    <Dialog open={true}
      onClose={handleClose}
      maxWidth='md'
      fullWidth>
      <Grid
        sx={{ m: '2px' }}>
        <Grid
          sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
          <X onClick={props.onClose} />
        </Grid>
        <Grid
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant='textXL'>{t('createTitle')}</Typography>
        </Grid>
      </Grid>
      <DialogContent>
        <Grid sx={{ pt: '30px' }}>
          <TextField
            placeholder={t('createTitle')}
            multiline
            fullWidth
            onChange={handleNameChange}
          />
        </Grid>
        <Grid sx={{ pt: '30px' }}>
          <TextField
            placeholder={t('description')}
            multiline
            rows={5}
            fullWidth
            onChange={handleDescriptionChange}
          />
        </Grid>
        <Grid sx={{ pt: '30px' }}>
          <FormControl
            fullWidth>
            <Select
              value={actionType}
              onChange={handleSelect}
              fullWidth>
              {actionTypes.map((option) => (
                <MenuItem key={option.description}
                  value={option.description}>
                  {option.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid sx={{ pt: '30px' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeInWasteLog}
                onChange={handleIncludeInWasteLogChange}
                disabled={!includeInWasteLog && props.wasteLogReasonsCount >= 4}
              />
            }
            label={<>{t('includeInWasteLog')}<Tooltip title={t('maxWasteReasonsError')}>
              <InfoIcon fontSize='small'
                sx={{ ml: 2, verticalAlign: 'sub' }} />
            </Tooltip></>}
          />
          {showMaxError && (
            <Typography color="error"
              variant="textSM"
              sx={{ mt: 1 }}>
              {t('maxWasteReasonsError')}
            </Typography>
          )}
        </Grid>
        {includeInWasteLog && (
          <Grid sx={{ pt: '20px' }}>
            <TextField
              label={t('wasteLogSortOrder')}
              type="number"
              fullWidth
              value={wasteLogSortOrder ?? ''}
              onChange={handleSortOrderChange}
              inputProps={{ min: 1, max: 4 }}
              helperText={t('wasteLogSortOrderHelper')}
              error={showDuplicateSortOrderError}
            />
            {showDuplicateSortOrderError && (
              <Typography color="error"
                variant="textSM"
                sx={{ mt: 1 }}>
                Another reason already uses this sort order. Please choose a different position.
              </Typography>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="secondary"
          onClick={handleClose}> {t('cancel')}</Button>
        <Button
          disabled={!name || !description || actionType === actionTypes[0].description}
          onClick={saveReasons}
        ><Plus />
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateReasonModal;